import React from 'react';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import * as XLSX from 'xlsx';

export const FONT = 'Mulish, sans-serif';

export const inr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

export const toISO = (d) => {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

export const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};

export const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'month', label: 'This month' },
  { key: 'year', label: 'This year' },
  { key: 'all', label: 'All time' },
];

export const presetRange = (key) => {
  const today = new Date();
  const start = new Date(today);
  switch (key) {
    case 'today':
      return { from: toISO(today), to: toISO(today) };
    case '7d':
      start.setDate(today.getDate() - 6);
      return { from: toISO(start), to: toISO(today) };
    case '30d':
      start.setDate(today.getDate() - 29);
      return { from: toISO(start), to: toISO(today) };
    case 'month':
      return { from: toISO(new Date(today.getFullYear(), today.getMonth(), 1)), to: toISO(today) };
    case 'year':
      return { from: toISO(new Date(today.getFullYear(), 0, 1)), to: toISO(today) };
    default:
      return { from: '', to: '' };
  }
};

export const rangeLabel = (from, to) =>
  from || to ? `${from ? fmtDate(from) : 'Start'} – ${to ? fmtDate(to) : 'Today'}` : 'All time';

const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

export const StatCard = ({ label, value, icon: Icon, gradient, sub, onClick, active, large }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: large ? 2.5 : 1.5,
      borderRadius: large ? '16px' : '12px',
      color: '#fff',
      background: gradient,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      gap: large ? 2 : 1.25,
      cursor: onClick ? 'pointer' : 'default',
      outline: active ? '3px solid #0f172a' : 'none',
      outlineOffset: 2,
      transition: 'transform 0.15s ease',
      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.10)',
      '&:hover': onClick ? { transform: 'translateY(-2px)' } : undefined,
    }}
  >
    <Box sx={{ background: 'rgba(255,255,255,0.22)', borderRadius: large ? '14px' : '10px', p: large ? 1.5 : 0.9, display: 'flex', flexShrink: 0 }}>
      <Icon size={large ? 28 : 20} />
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: large ? 13 : 11.5, fontWeight: 700, opacity: 0.92, fontFamily: FONT, ...ellipsis }}>{label}</Typography>
      <Tooltip title={value} placement="top" arrow>
        <Typography sx={{ fontSize: large ? 26 : 16, fontWeight: 800, fontFamily: FONT, lineHeight: 1.3, my: large ? 0.25 : 0, ...ellipsis }}>{value}</Typography>
      </Tooltip>
      {sub && (
        <Tooltip title={sub} placement="bottom" arrow>
          <Typography sx={{ fontSize: large ? 12 : 10.5, opacity: 0.88, fontFamily: FONT, ...ellipsis }}>{sub}</Typography>
        </Tooltip>
      )}
    </Box>
  </Paper>
);

export const statGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
  gap: 1.5,
  mb: 3,
};

export const exportButtonSx = (bg, hover) => ({
  textTransform: 'none',
  fontWeight: 700,
  fontFamily: FONT,
  borderRadius: '10px',
  px: 2,
  color: '#fff',
  background: bg,
  boxShadow: 'none',
  '&:hover': { background: hover, boxShadow: 'none' },
});

export const downloadCsv = (objects, filename) => {
  const csv = XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(objects));
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export const downloadExcel = (objects, filename, sheetName) => {
  const sheet = XLSX.utils.json_to_sheet(objects);
  sheet['!cols'] = Object.keys(objects[0] || {}).map(() => ({ wch: 18 }));
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, sheetName.slice(0, 31));
  XLSX.writeFile(book, `${filename}.xlsx`);
};

const esc = (v) =>
  String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// cells: array of rows, each an array of { value, className } (className: num | pos | neg | '').
export const openPrintView = ({ title, subtitle, headers, cells }) => {
  const head = headers.map((h) => `<th>${esc(h)}</th>`).join('');
  const body = cells
    .map((row) => `<tr>${row.map((c) => `<td class="${c.className || ''}">${esc(c.value)}</td>`).join('')}</tr>`)
    .join('');
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(`<!doctype html><html><head><title>${esc(title)}</title><style>
    body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
    h1{margin:0 0 4px;font-size:20px} p{margin:0 0 16px;color:#64748b;font-size:12px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th{background:#0f172a;color:#fff;text-align:left;padding:8px}
    td{padding:7px 8px;border-bottom:1px solid #e2e8f0}
    tr:nth-child(even) td{background:#f8fafc}
    .num{text-align:right}.pos{text-align:right;color:#16a34a;font-weight:700}.neg{text-align:right;color:#dc2626;font-weight:700}
    .warn{color:#dc2626;font-weight:700}
  </style></head><body><h1>${esc(title)}</h1><p>${esc(subtitle)}</p>
  <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`);
  win.document.close();
  win.focus();
  win.print();
};
